export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// fetch-based client (XHR hangs on some endpoints through the preview proxy)
// Duplicate concurrent GETs hang at the preview proxy (request coalescing), and React
// StrictMode double-fires effects in dev — so dedupe identical in-flight GETs.
const inflight = new Map();

const doFetch = async (method, url, body) => {
  const isForm = body instanceof FormData;
  const sep = url.includes('?') ? '&' : '?';
  const bust = `${sep}_t=${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const finalUrl = method === 'GET' ? `${API}${url}${bust}` : `${API}${url}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    return await fetch(finalUrl, {
      method,
      credentials: 'include',
      headers: body && !isForm ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? (isForm ? body : JSON.stringify(body)) : undefined,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
};

const request = async (method, url, body) => {
  const key = `${method} ${url}`;
  if (method === 'GET' && inflight.has(key)) return inflight.get(key);
  const promise = (async () => {
    let lastErr;
    for (let attempt = 0; attempt < 3; attempt++) {
      let res;
      try {
        res = await doFetch(method, url, body);
      } catch (e) {
        lastErr = e;
        continue; // timeout / network stall — retry with a fresh URL
      }
      let data = null;
      try { data = await res.json(); } catch (e) { /* empty body */ }
      if (!res.ok) {
        const err = new Error(`Request failed with status ${res.status}`);
        err.response = { status: res.status, data };
        throw err;
      }
      return { data };
    }
    throw lastErr;
  })();
  if (method === 'GET') {
    inflight.set(key, promise);
    promise.finally(() => inflight.delete(key)).catch(() => {});
  }
  return promise;
};

export const api = {
  get: (url) => request('GET', url),
  post: (url, body) => request('POST', url, body),
  put: (url, body) => request('PUT', url, body),
  delete: (url) => request('DELETE', url),
};

export function formatApiError(e) {
  if (e?.name === 'AbortError' || e?.message === 'Network Error' || /aborted|network|failed to fetch/i.test(e?.message || '')) {
    return 'Connection issue — please check your internet and try again.';
  }
  const detail = e?.response?.data?.detail;
  if (detail == null) return e?.message || 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((x) => x?.msg || JSON.stringify(x)).join(' ');
  return String(detail);
}

export const resolveMediaSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

// Lightweight variant for grid thumbnails (only affects backend-hosted uploads).
export const thumbSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/files/')) return `${process.env.REACT_APP_BACKEND_URL}${url}?thumb=1`;
  return resolveMediaSrc(url);
};
